import { cn, UzTooltip } from '@utazzo/ui';
import { CircleAlert, CircleCheck, Images, ImageOff } from 'lucide-react';
import React from 'react';

/**
 * "Is this row actually usable yet?" - the at-a-glance readiness chips shown on
 * the inventory listing screens (Activities, Transfers, Fixed Departures).
 *
 * A row can save perfectly well and still be unusable downstream: no photos to
 * put in an itinerary, no price to quote, no vendor to buy from. Status alone
 * doesn't say that - a row can sit "Active" with none of it - so ops had to open
 * each record to find out. These chips move that to the list.
 *
 * Green states carry the NUMBER (`5 images`), not just a tick, because "how
 * many" is the thing worth knowing once the answer is "some". Red states carry
 * the COUNT of what's absent with the names on hover, so the column stays narrow
 * while a row that needs work still says what it needs. The hover is a
 * zero-delay tooltip: scanning a column of chips means pointing at several in a
 * row, and a delay turns that into a wait each time.
 *
 * Each screen supplies its own checks - what makes an Activity usable is not
 * what makes a Transfer usable - so the rules live at the call sites, next to
 * the fields they read, and only the presentation is shared here.
 */

/** One pass/fail condition on a row. `label` is what the operator must go fix. */
export interface ReadinessCheck {
  label: string;
  ok: boolean;
}

const CHIP = 'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium';
const OK = 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200';
const MISSING = 'bg-rose-50 text-rose-700 ring-1 ring-rose-200';

export const ReadinessCell = ({
  photoCount,
  checks,
}: {
  /**
   * Omit entirely on a screen whose list API carries no image data - that is
   * different from `0`, which means "we asked and there are none" and correctly
   * shows red. All three inventory lists supply it today.
   */
  photoCount?: number;
  checks: ReadinessCheck[];
}): React.JSX.Element => {
  const missing = checks.filter((check) => !check.ok);

  return (
    <div className="flex flex-col items-start gap-1">
      {photoCount !== undefined &&
        (photoCount > 0 ? (
          <span className={cn(CHIP, OK)}>
            <Images className="h-3 w-3 shrink-0" />
            {photoCount} image{photoCount === 1 ? '' : 's'}
          </span>
        ) : (
          <span className={cn(CHIP, MISSING)}>
            <ImageOff className="h-3 w-3 shrink-0" />
            No images
          </span>
        ))}

      {missing.length === 0 ? (
        // "Info complete", not "Complete": this chip answers the FIELDS half
        // only, the same bar the list's Information filter uses. Images are the
        // other half and already have their own chip above, so a row can
        // honestly read "No images" + "Info complete" without contradicting
        // itself.
        <span className={cn(CHIP, OK)}>
          <CircleCheck className="h-3 w-3 shrink-0" />
          Info complete
        </span>
      ) : (
        // tabIndex on the chip so the names are reachable by keyboard focus,
        // not hover-only.
        <UzTooltip delay={0} label={`Missing: ${missing.map((check) => check.label).join(', ')}`}>
          <span className={cn(CHIP, MISSING)} tabIndex={0}>
            <CircleAlert className="h-3 w-3 shrink-0" />
            {missing.length} missing
          </span>
        </UzTooltip>
      )}
    </div>
  );
};
